import { useEffect, useState } from "react";
import { useI18n } from "@wato/i18n/react";
import { useApi } from "../../../api/context";
import type { Member } from "../../../api/types";
import type { CsatSummaryView } from "../../../api/analytics-types";
import { ChartCard, EmptyData, KpiCard } from "../../../components/analytics/cards";
import { useWorkspace } from "../../workspace/WorkspaceContext";

/** FEAT-06: average CSAT rating + number of ratings for the selected range. */
export function CsatCard({ from, to }: { from: string; to: string }) {
  const { t, locale } = useI18n();
  const api = useApi();
  const { workspace } = useWorkspace();
  const [state, setState] = useState<
    { status: "loading" } | { status: "error" } | { status: "ready"; summary: CsatSummaryView }
  >({ status: "loading" });

  const [members, setMembers] = useState<readonly Member[]>([]);
  useEffect(() => {
    let live = true;
    api
      .listMembers(workspace.id)
      .then((m) => live && setMembers(m))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [api, workspace.id]);

  useEffect(() => {
    let live = true;
    setState({ status: "loading" });
    api
      .getCsatSummary(workspace.id, { from, to })
      .then((summary) => live && setState({ status: "ready", summary }))
      .catch(() => live && setState({ status: "error" }));
    return () => {
      live = false;
    };
  }, [api, workspace.id, from, to]);

  const num = new Intl.NumberFormat(locale === "ar" ? "ar-u-nu-latn" : "en", {
    maximumFractionDigits: 1,
  });
  return (
    <div data-testid="csat-card">
      <ChartCard title={t("analyticsPlus.csat.title")}>
        {state.status === "loading" ? null : state.status === "error" ? (
          <p role="alert" className="text-sm text-danger-fg">
            {t("analyticsPlus.csat.error")}
          </p>
        ) : state.summary.count === 0 || state.summary.average === null ? (
          <EmptyData hint={t("analyticsPlus.csat.empty")} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <KpiCard
                label={t("analyticsPlus.csat.average")}
                value={num.format(state.summary.average)}
                valueTestId="csat-average"
              />
              <KpiCard
                label={t("analyticsPlus.csat.count")}
                value={num.format(state.summary.count)}
                valueTestId="csat-count"
              />
            </div>
            {(state.summary.byAgent?.length ?? 0) > 0 && (
              <table className="mt-3 w-full text-sm" data-testid="csat-by-agent">
                <caption className="pb-1 text-start text-xs text-fg-muted">
                  {t("analyticsPlus.csat.byAgent")}
                </caption>
                <tbody>
                  {(state.summary.byAgent ?? []).map((a) => {
                    const m = members.find((x) => x.userId === a.agentUserId);
                    return (
                      <tr key={a.agentUserId ?? "none"}>
                        <td className="py-1 text-start">
                          {a.agentUserId === null
                            ? t("analyticsPlus.csat.noAgent")
                            : (m?.displayName ?? m?.email ?? a.agentUserId)}
                        </td>
                        <td className="py-1 text-end">{num.format(a.average)}</td>
                        <td className="py-1 text-end text-fg-muted">{num.format(a.count)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </ChartCard>
    </div>
  );
}
